import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { desktop } from '@/lib/printTransport'
import { getStationPrinterConfig, setStationPrinterConfig } from '@/lib/stationPrinterMap'
import { seedStationTargetFields, stationTargetForSave } from '@/lib/stationPrinterTarget'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Plus,
  Trash2,
  Edit2,
  Monitor,
  Printer,
  X,
  Store,
  AlertTriangle,
  GripVertical,
  Check,
  ChefHat,
  Send,
  ArrowRight,
  Search,
  Loader2,
} from 'lucide-react'
import type { KitchenStation, Category, StationKOT } from '@/types'
import { useKitchenSettings } from '@/hooks/useKitchenSettings'
import { useToast } from '@/hooks/use-toast'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { TestPrinterButton } from '@/components/admin/TestPrinterButton'
import { OptionCards } from '@/components/admin/kitchen-setup/OptionCards'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type StationFormState = {
  name: string
  output_type: 'kds' | 'printer'
  print_location: 'kitchen' | 'counter'
  printer_target: string
  /** LAN ip:port for the Server APK (F-FE-N155). Desktop only; elsewhere printer_target carries both. */
  network_target: string
  /** Paper width in mm for this station's printer (machine-local). */
  printer_width_mm: number
  sort_order: number
  is_active: boolean
}

const EMPTY_FORM: StationFormState = {
  name: '',
  output_type: 'kds',
  print_location: 'kitchen',
  printer_target: '',
  network_target: '',
  printer_width_mm: 80,
  sort_order: 0,
  is_active: true,
}

/** The filter box only earns its place once the board is too long to scan. */
const FILTER_MIN_CATEGORIES = 9

const DEFAULT_CATEGORY_COLOR = '#3b82f6'

function stationOwnerOf(stations: KitchenStation[], categoryId: string): KitchenStation | undefined {
  return stations.find((s) => (s.category_ids || []).includes(categoryId))
}

/**
 * Resolve which station an item routed via `categoryId` would land on, mirroring
 * the backend logic in kot.go FireKOT (active stations, then deterministic fallback).
 */
function resolveRoutedStation(
  stations: KitchenStation[],
  categoryId: string | null,
): { station: KitchenStation; isFallback: boolean } | null {
  const active = stations.filter((s) => s.is_active)
  if (categoryId) {
    const owner = active.find((s) => (s.category_ids || []).includes(categoryId))
    if (owner) return { station: owner, isFallback: false }
  }
  if (active.length === 0) return null
  // (name='Main Kitchen') DESC, sort_order ASC, created_at ASC
  const sorted = [...active].sort((a, b) => {
    const an = a.name === 'Main Kitchen' ? 0 : 1
    const bn = b.name === 'Main Kitchen' ? 0 : 1
    if (an !== bn) return an - bn
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    return (a.created_at || '').localeCompare(b.created_at || '')
  })
  return { station: sorted[0], isFallback: true }
}

const countOf = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type StationManagementVariant = 'page' | 'embedded'

export function StationManagement({
  variant = 'page',
  kitchenMode,
}: {
  variant?: StationManagementVariant
  /**
   * The mode to draw the board for. Settings passes the mode being edited so
   * the board shows what a switch would do before it is saved; without it the
   * saved venue mode is used.
   */
  kitchenMode?: 'kds' | 'kot_only'
} = {}) {
  const isEmbedded = variant === 'embedded'
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const kitchen = useKitchenSettings()
  const isKotOnly = (kitchenMode ?? kitchen.mode) === 'kot_only'

  const [showCreate, setShowCreate] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState<StationFormState>(EMPTY_FORM)
  const [pendingDelete, setPendingDelete] = useState<KitchenStation | null>(null)
  const [pendingMove, setPendingMove] = useState<{
    category: Category
    fromStation: KitchenStation
    toStation: KitchenStation
  } | null>(null)
  const [filter, setFilter] = useState('')

  // ── Queries ──
  const {
    data: stations = [],
    isLoading: stationsLoading,
    isError: stationsError,
    error: stationsErrorDetail,
  } = useQuery({
    queryKey: ['stations'],
    queryFn: async () => {
      const res = await apiClient.getStations()
      return (res.data || []) as KitchenStation[]
    },
  })

  const { data: categories = [] } = useCategories()

  // ── Derived ──
  const sortedStations = useMemo(
    () => [...stations].sort((a, b) => a.sort_order - b.sort_order),
    [stations],
  )
  const activeStations = useMemo(() => sortedStations.filter((s) => s.is_active), [sortedStations])

  const unassignedCategories = useMemo(
    () => categories.filter((c) => !stations.some((s) => (s.category_ids || []).includes(c.id))),
    [categories, stations],
  )

  /** Where anything without a working station of its own ends up. */
  const fallbackStation = useMemo(
    () => resolveRoutedStation(sortedStations, null)?.station,
    [sortedStations],
  )

  // ── Mutations ──
  const invalidateStations = () => {
    queryClient.invalidateQueries({ queryKey: ['stations'] })
    queryClient.invalidateQueries({ queryKey: ['categories'] })
    queryClient.invalidateQueries({ queryKey: ['admin-categories'] })
    queryClient.invalidateQueries({ queryKey: ['admin', 'stations', 'list-for-settings'] })
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      const target = stationTargetForSave({
        desktop: !!desktop(),
        device: form.printer_target,
        networkTarget: form.network_target,
      })
      const res = await apiClient.createStation({
        name: form.name.trim(),
        output_type: form.output_type,
        sort_order: form.sort_order,
        ...(form.output_type === 'printer'
          ? { print_location: form.print_location, printer_target: target.backendTarget }
          : {}),
      })
      if (!res.success) throw new Error(res.message || 'Failed to create station')
      // is_active defaults true on insert; only push an update if user toggled it off.
      if (!form.is_active && res.data?.id) {
        await apiClient.updateStation(res.data.id, { is_active: false })
      }
      // Persist the printer device + width to this machine's local map (works
      // without a backend deploy; the source of truth for routing on this
      // terminal).
      if (res.data?.id) {
        setStationPrinterConfig(res.data.id, {
          device: form.output_type === 'printer' ? target.localDevice : '',
          paperWidthMm: form.output_type === 'printer' ? form.printer_width_mm : undefined,
        })
      }
    },
    onSuccess: () => {
      invalidateStations()
      resetForm()
      toast({ title: 'Station added' })
    },
    onError: (e: unknown) =>
      toast({
        title: 'Could not add station',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      }),
  })

  const updateMutation = useMutation({
    mutationFn: async (id: string) => {
      const target = stationTargetForSave({
        desktop: !!desktop(),
        device: form.printer_target,
        networkTarget: form.network_target,
      })
      const res = await apiClient.updateStation(id, {
        name: form.name.trim(),
        output_type: form.output_type,
        sort_order: form.sort_order,
        is_active: form.is_active,
        print_location: form.output_type === 'printer' ? form.print_location : 'kitchen',
        printer_target: form.output_type === 'printer' ? target.backendTarget : '',
      })
      if (!res.success) throw new Error(res.message || 'Failed to update station')
      setStationPrinterConfig(id, {
        device: form.output_type === 'printer' ? target.localDevice : '',
        paperWidthMm: form.output_type === 'printer' ? form.printer_width_mm : undefined,
      })
    },
    onSuccess: () => {
      invalidateStations()
      resetForm()
      toast({ title: 'Station saved' })
    },
    onError: (e: unknown) =>
      toast({
        title: 'Could not save station',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteStation(id),
    onSuccess: () => {
      invalidateStations()
      setPendingDelete(null)
      toast({ title: 'Station deleted' })
    },
    onError: (e: unknown) =>
      toast({
        title: 'Could not delete station',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      }),
  })

  const setCategoriesMutation = useMutation({
    mutationFn: ({ stationId, categoryIds }: { stationId: string; categoryIds: string[] }) =>
      apiClient.setStationCategories(stationId, categoryIds),
    onSuccess: invalidateStations,
    onError: (e: unknown) =>
      toast({
        title: 'Could not update categories',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      }),
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      apiClient.updateStation(id, { is_active }),
    // Optimistic update so the switch doesn't lag.
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: ['stations'] })
      const prev = queryClient.getQueryData<KitchenStation[]>(['stations'])
      if (prev) {
        queryClient.setQueryData<KitchenStation[]>(
          ['stations'],
          prev.map((s) => (s.id === id ? { ...s, is_active } : s)),
        )
      }
      return { prev }
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['stations'], ctx.prev)
      toast({ title: 'Could not update station', variant: 'destructive' })
    },
    onSettled: invalidateStations,
  })

  const reorderMutation = useMutation({
    mutationFn: async (newOrder: KitchenStation[]) => {
      // Issue updates in parallel; backend updates `sort_order` per row.
      await Promise.all(
        newOrder.map((s, idx) =>
          s.sort_order === idx ? Promise.resolve() : apiClient.updateStation(s.id, { sort_order: idx }),
        ),
      )
    },
    // Show the new order at once; the rows would otherwise snap back until the refetch lands.
    onMutate: async (newOrder) => {
      await queryClient.cancelQueries({ queryKey: ['stations'] })
      const prev = queryClient.getQueryData<KitchenStation[]>(['stations'])
      queryClient.setQueryData<KitchenStation[]>(
        ['stations'],
        newOrder.map((s, idx) => ({ ...s, sort_order: idx })),
      )
      return { prev }
    },
    onError: (e: unknown, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['stations'], ctx.prev)
      toast({
        title: 'Could not reorder stations',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      })
    },
    onSettled: invalidateStations,
  })

  const testKotMutation = useMutation({
    mutationFn: (stationId: string) => apiClient.testStationKOT(stationId),
    onSuccess: (res) => {
      if (!res.success) {
        toast({
          title: 'Test failed',
          description: res.message || 'Could not generate a test KOT',
          variant: 'destructive',
        })
        return
      }
      const kots = (res.data?.kots || []) as StationKOT[]
      const cached = queryClient.getQueryData<{ data?: Record<string, unknown> }>(['settings', 'all'])
      const cfg = parseReceiptSettings(cached?.data)
      printKotReceipts(kots, cfg)
      toast({ title: 'Test KOT sent', description: 'Opening print dialog…' })
    },
    onError: (e: unknown) =>
      toast({
        title: 'Test failed',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      }),
  })

  // ── Helpers ──
  const resetForm = () => {
    setShowCreate(false)
    setEditId(null)
    setForm(EMPTY_FORM)
  }

  const startCreate = () => {
    resetForm()
    setForm({ ...EMPTY_FORM, sort_order: stations.length })
    setShowCreate(true)
  }

  const startEdit = (s: KitchenStation) => {
    setShowCreate(false)
    setEditId(s.id)
    const local = getStationPrinterConfig(s.id)
    const target = seedStationTargetFields({
      desktop: !!desktop(),
      localDevice: local.device,
      existingTarget: s.printer_target || '',
    })
    setForm({
      name: s.name,
      output_type: s.output_type,
      print_location: s.print_location === 'counter' ? 'counter' : 'kitchen',
      printer_target: target.device,
      network_target: target.networkTarget,
      printer_width_mm: local.paperWidthMm && local.paperWidthMm > 0 ? local.paperWidthMm : 80,
      sort_order: s.sort_order,
      is_active: s.is_active,
    })
  }

  const removeCategoryFromStation = (station: KitchenStation, categoryId: string) => {
    const next = (station.category_ids || []).filter((id) => id !== categoryId)
    setCategoriesMutation.mutate({ stationId: station.id, categoryIds: next })
  }

  const assignCategoryToStation = (station: KitchenStation, category: Category) => {
    const owner = stationOwnerOf(stations, category.id)
    if (owner && owner.id !== station.id) {
      setPendingMove({ category, fromStation: owner, toStation: station })
      return
    }
    const ids = Array.from(new Set([...(station.category_ids || []), category.id]))
    setCategoriesMutation.mutate({ stationId: station.id, categoryIds: ids })
  }

  const confirmMove = () => {
    if (!pendingMove) return
    const { category, toStation } = pendingMove
    const ids = Array.from(new Set([...(toStation.category_ids || []), category.id]))
    setCategoriesMutation.mutate(
      { stationId: toStation.id, categoryIds: ids },
      { onSettled: () => setPendingMove(null) },
    )
  }

  // ── Drag-to-reorder ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const fromIdx = sortedStations.findIndex((s) => s.id === active.id)
    const toIdx = sortedStations.findIndex((s) => s.id === over.id)
    if (fromIdx < 0 || toIdx < 0) return
    reorderMutation.mutate(arrayMove(sortedStations, fromIdx, toIdx))
  }

  const editingStation = editId ? stations.find((s) => s.id === editId) : undefined
  const needle = filter.trim().toLowerCase()

  // ── Loading / error ──
  if (stationsLoading) {
    return (
      <div
        className={`flex items-center justify-center min-h-[240px] ${isEmbedded ? '' : 'p-6'}`}
      >
        <div className="text-muted-foreground text-sm">Loading kitchen stations…</div>
      </div>
    )
  }

  if (stationsError) {
    return (
      <div className={`space-y-2 ${isEmbedded ? '' : 'p-6'}`}>
        {isEmbedded ? (
          <h3 className="text-lg font-semibold tracking-tight">Stations</h3>
        ) : (
          <h2 className="text-3xl font-bold tracking-tight">Kitchen stations</h2>
        )}
        <p className="text-destructive text-sm">
          Could not load stations.{' '}
          {stationsErrorDetail instanceof Error
            ? stationsErrorDetail.message
            : 'Check that the API is running and the database has the kitchen_stations migration applied.'}
        </p>
      </div>
    )
  }

  return (
    <section
      aria-labelledby="stations-heading"
      className={cn('space-y-4', !isEmbedded && 'max-w-5xl p-6')}
    >
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          {isEmbedded ? (
            <h3 id="stations-heading" className="text-lg font-semibold tracking-tight">
              Stations
            </h3>
          ) : (
            <h2 id="stations-heading" className="text-3xl font-bold tracking-tight">
              Kitchen stations
            </h2>
          )}
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Each menu category goes to one station when an order is fired. Changes here save as
            you make them.
          </p>
        </div>
        {sortedStations.length > 0 && (
          <Button onClick={startCreate}>
            <Plus className="mr-2 h-4 w-4" /> Add station
          </Button>
        )}
      </div>

      {/* Settings has the mode picker right above the board; the standalone page does not. */}
      {isKotOnly && !isEmbedded && (
        <p className="flex items-start gap-2 rounded-lg border border-warning-border bg-warning-surface px-3 py-2.5 text-sm text-warning-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The restaurant is set to printed KOTs only, so every station prints a slip and the
            Kitchen Display is hidden. Change this in{' '}
            <Link to="/admin/settings" search={{ section: 'kitchen' }} className="font-medium underline">
              Settings, Kitchen Setup
            </Link>
            .
          </span>
        </p>
      )}

      {sortedStations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-6 py-12 text-center">
          <ChefHat className="mx-auto h-9 w-9 text-muted-foreground" />
          <p className="mt-3 font-medium">No stations yet</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            A station is a screen or a printer in your kitchen. Add one so fired orders have
            somewhere to go.
          </p>
          <Button onClick={startCreate} className="mt-5">
            <Plus className="mr-2 h-4 w-4" /> Add your first station
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <BoardSummary
            totalCategories={categories.length}
            unassignedCount={unassignedCategories.length}
            activeStationCount={activeStations.length}
            fallbackStation={fallbackStation}
            filter={filter}
            onFilterChange={setFilter}
          />
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={sortedStations.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul>
                {sortedStations.map((s) => (
                  <StationRow
                    key={s.id}
                    station={s}
                    categories={categories}
                    stations={sortedStations}
                    activeStations={activeStations}
                    fallbackStation={fallbackStation}
                    caughtCategories={fallbackStation?.id === s.id ? unassignedCategories : []}
                    isKotOnly={isKotOnly}
                    needle={needle}
                    onEdit={() => startEdit(s)}
                    onDelete={() => setPendingDelete(s)}
                    onToggleActive={(v) => toggleActiveMutation.mutate({ id: s.id, is_active: v })}
                    onRemoveCategory={(cid) => removeCategoryFromStation(s, cid)}
                    onAssignCategory={(c) => assignCategoryToStation(s, c)}
                    onPlaceCategory={(c, target) => assignCategoryToStation(target, c)}
                    onTestKot={() => testKotMutation.mutate(s.id)}
                    testKotPending={testKotMutation.isPending}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <StationSheet
        open={showCreate || !!editId}
        editingName={editingStation?.name}
        isKotOnly={isKotOnly}
        form={form}
        setForm={setForm}
        onCancel={resetForm}
        onSubmit={() => (editId ? updateMutation.mutate(editId) : createMutation.mutate())}
        submitting={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteStationDialog
        station={pendingDelete}
        categories={categories}
        stations={sortedStations}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
        deleting={deleteMutation.isPending}
      />

      <MoveCategoryDialog
        pendingMove={pendingMove}
        onCancel={() => setPendingMove(null)}
        onConfirm={confirmMove}
        moving={setCategoriesMutation.isPending}
      />
    </section>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * The one-line verdict on the whole board. Categories without a station are
 * only a problem when there is more than one place they could have gone.
 */
function BoardSummary({
  totalCategories,
  unassignedCount,
  activeStationCount,
  fallbackStation,
  filter,
  onFilterChange,
}: {
  totalCategories: number
  unassignedCount: number
  activeStationCount: number
  fallbackStation: KitchenStation | undefined
  filter: string
  onFilterChange: (v: string) => void
}) {
  let tone: 'ok' | 'warn' | 'plain' = 'plain'
  let text: string
  if (!fallbackStation) {
    tone = 'warn'
    text = 'Every station is switched off, so fired orders have nowhere to go.'
  } else if (totalCategories === 0) {
    text = 'No menu categories yet. Add them under Menu, then place them here.'
  } else if (unassignedCount === 0) {
    tone = 'ok'
    text = `All ${countOf(totalCategories, 'category has', 'categories have')} a station.`
  } else if (activeStationCount === 1) {
    text = `Everything goes to ${fallbackStation.name}, your only station.`
  } else {
    tone = 'warn'
    text = `${countOf(unassignedCount, 'category has', 'categories have')} no station and ${
      unassignedCount === 1 ? 'goes' : 'go'
    } to ${fallbackStation.name}.`
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-border px-4 py-3 sm:px-5">
      <p
        className={cn(
          'flex items-start gap-2 text-sm',
          tone === 'warn' && 'text-warning-fg',
          tone === 'ok' && 'text-success-fg',
        )}
      >
        {tone === 'warn' && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        {tone === 'ok' && <Check className="mt-0.5 h-4 w-4 shrink-0" />}
        <span className={tone === 'plain' ? 'text-muted-foreground' : 'font-medium'}>{text}</span>
      </p>
      {totalCategories >= FILTER_MIN_CATEGORIES && (
        <div className="relative w-full sm:w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Find a category"
            aria-label="Find a category"
            className="pl-9"
          />
        </div>
      )}
    </div>
  )
}

function CategoryDot({ color }: { color?: string }) {
  return (
    <span
      aria-hidden
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color || DEFAULT_CATEGORY_COLOR }}
    />
  )
}

/** How a station's tickets come out, in the words used on the row. */
function stationOutput(
  station: KitchenStation,
  isKotOnly: boolean,
): { icon: typeof Monitor; label: string; forced: boolean } {
  if (station.output_type === 'kds' && !isKotOnly) {
    return { icon: Monitor, label: 'Kitchen screen', forced: false }
  }
  if (station.output_type === 'kds') {
    return { icon: Printer, label: 'Prints a slip while printed KOTs only is on', forced: true }
  }
  return {
    icon: Printer,
    label: station.print_location === 'counter' ? 'Prints at the counter' : 'Prints at the station',
    forced: false,
  }
}

function StationRow({
  station,
  categories,
  stations,
  activeStations,
  fallbackStation,
  caughtCategories,
  isKotOnly,
  needle,
  onEdit,
  onDelete,
  onToggleActive,
  onRemoveCategory,
  onAssignCategory,
  onPlaceCategory,
  onTestKot,
  testKotPending,
}: {
  station: KitchenStation
  categories: Category[]
  stations: KitchenStation[]
  activeStations: KitchenStation[]
  fallbackStation: KitchenStation | undefined
  /** Categories with no station, shown on the row that catches them. */
  caughtCategories: Category[]
  isKotOnly: boolean
  /** Lower-cased filter text; chips that don't match are dimmed. */
  needle: string
  onEdit: () => void
  onDelete: () => void
  onToggleActive: (v: boolean) => void
  onRemoveCategory: (categoryId: string) => void
  onAssignCategory: (cat: Category) => void
  onPlaceCategory: (cat: Category, target: KitchenStation) => void
  onTestKot: () => void
  testKotPending: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: station.id,
  })
  const assignedCategories = categories.filter((c) => (station.category_ids || []).includes(c.id))
  const otherCategories = categories.filter((c) => !(station.category_ids || []).includes(c.id))
  const output = stationOutput(station, isKotOnly)
  const OutputIcon = output.icon
  const isFallback = fallbackStation?.id === station.id
  const dimmed = (name: string) => needle !== '' && !name.toLowerCase().includes(needle)

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'border-t border-border px-4 py-4 first:border-t-0 sm:px-5',
        isDragging && 'relative z-10 rounded-lg bg-card shadow-lg ring-1 ring-border',
      )}
    >
      <div className="flex items-start gap-2 sm:gap-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="-ml-1.5 mt-0.5 cursor-grab touch-none rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
          aria-label={`Reorder ${station.name}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className={cn('min-w-0', !station.is_active && 'opacity-60')}>
              <h4 className="break-words text-base font-semibold leading-6">{station.name}</h4>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span className={cn('inline-flex items-center gap-1.5', output.forced && 'text-warning-fg')}>
                  <OutputIcon className="h-3.5 w-3.5 shrink-0" />
                  {output.label}
                </span>
                {!station.is_active && (
                  <span className="rounded border border-border px-1.5 text-xs font-medium text-foreground">
                    Off
                  </span>
                )}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {/* A test slip only means something where a slip is printed. */}
              {(station.output_type === 'printer' || isKotOnly) && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="mr-2 text-muted-foreground"
                  onClick={onTestKot}
                  disabled={!station.is_active || testKotPending}
                >
                  <Send className="mr-2 h-3.5 w-3.5" /> Send test KOT
                </Button>
              )}
              <Switch
                checked={station.is_active}
                onCheckedChange={onToggleActive}
                aria-label={`${station.name} is on`}
                className="mr-1"
              />
              <button
                type="button"
                onClick={onEdit}
                className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Edit ${station.name}`}
                title="Edit"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Delete ${station.name}`}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {assignedCategories.map((cat) => (
              <span
                key={cat.id}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border border-border bg-background py-1 pl-2.5 pr-1 text-sm transition-opacity',
                  dimmed(cat.name) && 'opacity-30',
                )}
              >
                <CategoryDot color={cat.color} />
                {cat.name}
                <button
                  type="button"
                  onClick={() => onRemoveCategory(cat.id)}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Take ${cat.name} off ${station.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <AddCategoryPopover
              station={station}
              otherCategories={otherCategories}
              stations={stations}
              onAdd={onAssignCategory}
              first={assignedCategories.length === 0}
            />
          </div>

          {!station.is_active && assignedCategories.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {fallbackStation
                ? `While this station is off, its categories go to ${fallbackStation.name}.`
                : 'While this station is off, its categories have nowhere to go.'}
            </p>
          )}

          {isFallback && caughtCategories.length > 0 && (
            <div className="space-y-2 rounded-md bg-muted/50 px-3 py-2.5">
              <p className="text-sm text-muted-foreground">
                {activeStations.length === 1
                  ? 'No station chosen for these, so they come here:'
                  : 'No station chosen for these, so they come here. Pick one to place it:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {caughtCategories.map((cat) => (
                  <PlaceCategoryPopover
                    key={cat.id}
                    category={cat}
                    stations={activeStations}
                    dimmed={dimmed(cat.name)}
                    onPlace={(target) => onPlaceCategory(cat, target)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

/** A category nobody has placed yet. Click it to give it a station. */
function PlaceCategoryPopover({
  category,
  stations,
  dimmed,
  onPlace,
}: {
  category: Category
  stations: KitchenStation[]
  dimmed: boolean
  onPlace: (station: KitchenStation) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/50 bg-background px-2.5 py-1 text-sm transition-opacity hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            dimmed && 'opacity-30',
          )}
        >
          <CategoryDot color={category.color} />
          {category.name}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2">
        <p className="px-2 pb-2 text-xs text-muted-foreground">Send {category.name} to</p>
        {stations.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              onPlace(s)
              setOpen(false)
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            {s.output_type === 'kds' ? (
              <Monitor className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate">{s.name}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

function AddCategoryPopover({
  station,
  otherCategories,
  stations,
  onAdd,
  first,
}: {
  station: KitchenStation
  otherCategories: Category[]
  stations: KitchenStation[]
  onAdd: (cat: Category) => void
  /** True when the station has no categories yet, so the button says more. */
  first: boolean
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  // Split into "free" (unassigned) vs "would move" so the picker is honest about
  // what clicking will do.
  const splits = useMemo(() => {
    const free: Category[] = []
    const owned: Array<{ cat: Category; from: KitchenStation }> = []
    for (const c of otherCategories) {
      const owner = stationOwnerOf(stations, c.id)
      if (owner) owned.push({ cat: c, from: owner })
      else free.push(c)
    }
    const f = filter.trim().toLowerCase()
    return {
      free: f ? free.filter((c) => c.name.toLowerCase().includes(f)) : free,
      owned: f ? owned.filter((o) => o.cat.name.toLowerCase().includes(f)) : owned,
    }
  }, [otherCategories, stations, filter])

  if (otherCategories.length === 0) return null

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setFilter('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            first && '-ml-2',
          )}
        >
          <Plus className="h-3.5 w-3.5" />
          {first ? 'Choose categories' : 'Add'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        <Input
          autoFocus
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search categories"
          aria-label="Search categories"
          className="mb-2"
        />
        <div className="max-h-64 space-y-3 overflow-y-auto">
          {splits.free.length > 0 && (
            <div>
              <p className="mb-1 px-2 text-xs text-muted-foreground">No station yet</p>
              <div className="space-y-0.5">
                {splits.free.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onAdd(c)
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                  >
                    <CategoryDot color={c.color} />
                    <span className="truncate">{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {splits.owned.length > 0 && (
            <div>
              <p className="mb-1 px-2 text-xs text-muted-foreground">Move from another station</p>
              <div className="space-y-0.5">
                {splits.owned.map(({ cat, from }) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => {
                      onAdd(cat)
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                  >
                    <CategoryDot color={cat.color} />
                    <span className="min-w-0 flex-1 truncate">{cat.name}</span>
                    <span className="max-w-[45%] shrink-0 truncate text-xs text-muted-foreground">
                      on {from.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {splits.free.length === 0 && splits.owned.length === 0 && (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              No category matches. {station.name} may already have it.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function FormField({
  label,
  optional,
  htmlFor,
  hint,
  children,
}: {
  label: string
  optional?: boolean
  htmlFor?: string
  hint?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium">
        {label}
        {optional && <span className="font-normal text-muted-foreground"> (optional)</span>}
      </label>
      {children}
      {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function StationSheet({
  open,
  editingName,
  isKotOnly,
  form,
  setForm,
  onCancel,
  onSubmit,
  submitting,
}: {
  open: boolean
  /** Saved name of the station being edited; undefined when adding one. */
  editingName: string | undefined
  isKotOnly: boolean
  form: StationFormState
  setForm: (s: StationFormState) => void
  onCancel: () => void
  onSubmit: () => void
  submitting: boolean
}) {
  const editing = editingName !== undefined
  const canSubmit = form.name.trim() !== '' && !submitting
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onCancel()}>
      <SheetContent size="lg">
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) onSubmit()
          }}
        >
          <SheetHeader>
            <SheetTitle>{editing ? `Edit ${editingName}` : 'Add a station'}</SheetTitle>
            <SheetDescription>
              A station is one screen or one printer in your kitchen.
            </SheetDescription>
          </SheetHeader>

          <SheetBody className="space-y-6">
            <FormField label="Name" htmlFor="station-name">
              <Input
                id="station-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Grill, Bar, Tandoor"
                autoComplete="off"
              />
            </FormField>

            <div>
              <p className="mb-1.5 text-sm font-medium">Tickets come out on</p>
              <OptionCards
                name="station-output"
                label="Tickets come out on"
                value={form.output_type}
                onChange={(output_type) =>
                  setForm(
                    output_type === 'kds'
                      ? { ...form, output_type, print_location: 'kitchen' }
                      : { ...form, output_type },
                  )
                }
                className="sm:grid-cols-2"
                options={[
                  {
                    value: 'kds',
                    title: 'Kitchen screen',
                    icon: Monitor,
                    description: 'Items show on the display until a cook bumps them.',
                  },
                  {
                    value: 'printer',
                    title: 'Printer',
                    icon: Printer,
                    description: 'A slip prints when the order is fired.',
                  },
                ]}
              />
              {isKotOnly && (
                <p className="mt-2 text-xs text-muted-foreground">
                  The restaurant is on printed KOTs only, so this station prints either way. Your
                  choice takes effect if you switch to the kitchen display.
                </p>
              )}
            </div>

            {form.output_type === 'printer' && (
              <>
                <div>
                  <p className="mb-1.5 text-sm font-medium">The slip prints</p>
                  <OptionCards
                    name="station-print-location"
                    label="The slip prints"
                    value={form.print_location}
                    onChange={(print_location) => setForm({ ...form, print_location })}
                    className="sm:grid-cols-2"
                    options={[
                      {
                        value: 'kitchen',
                        title: 'At the station',
                        icon: Printer,
                        description: 'On the printer next to that pass.',
                      },
                      {
                        value: 'counter',
                        title: 'At the counter',
                        icon: Store,
                        description: 'Up front. Staff walk it to the kitchen.',
                      },
                    ]}
                  />
                  {form.print_location === 'counter' && (
                    <p className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                      When a waiter fires this station from a tablet, the ticket is sent to the
                      counter terminal to print — tablets can&apos;t reach a printer plugged into
                      the counter PC. One counter PC must have{' '}
                      <strong>Print tablet KOTs on this PC</strong> switched on under Admin →
                      Settings, or these tickets queue up unprinted.
                    </p>
                  )}
                </div>

                <FormField
                  label={desktop() ? 'Printer on this PC' : 'Printer device'}
                  optional
                  hint={
                    desktop() ? (
                      <>
                        Which of this computer&apos;s printers this station&apos;s slips go to.
                        Stored on this PC only; leave blank for the default printer.
                      </>
                    ) : (
                      <>
                        Which physical printer this station&apos;s slips go to. Leave blank for the
                        phone agent&apos;s default IP. Use LAN <code>ip</code> or{' '}
                        <code>ip:port</code> (e.g. <code>192.168.18.120:9100</code>) so Kitchen and
                        Rooftop route to different thermal printers.
                      </>
                    )
                  }
                >
                  <PrinterTargetField
                    value={form.printer_target}
                    onChange={(v) => setForm({ ...form, printer_target: v })}
                  />
                </FormField>
                {/*
                  F-AND-N1: prove the address before saving it. A station target is
                  free text with no validation anywhere in the stack, and a wrong
                  one fails at fire time in front of a customer.
                */}
                <div className="-mt-3">
                  <TestPrinterButton
                    target={form.printer_target}
                    label={form.name?.trim() || 'Station'}
                  />
                </div>

                {desktop() && (
                  <FormField
                    label="Network printer for tablets"
                    optional
                    htmlFor="station-network-target"
                    hint={
                      <>
                        LAN <code>ip:port</code> the Bhookly Android app prints this station to.
                        Saved to the restaurant, not this PC — this computer ignores it and uses
                        the printer above. Prove it with <strong>Test this printer</strong> on the
                        tablet.
                      </>
                    }
                  >
                    <Input
                      id="station-network-target"
                      value={form.network_target}
                      onChange={(e) => setForm({ ...form, network_target: e.target.value })}
                      placeholder="192.168.18.120:9100"
                    />
                  </FormField>
                )}

                <div>
                  <p className="mb-1.5 text-sm font-medium">Paper width</p>
                  <OptionCards
                    name="station-paper-width"
                    label="Paper width"
                    size="sm"
                    value={form.printer_width_mm}
                    onChange={(printer_width_mm) => setForm({ ...form, printer_width_mm })}
                    className="grid-cols-2 sm:max-w-xs"
                    options={[
                      { value: 58, title: '58 mm' },
                      { value: 80, title: '80 mm' },
                    ]}
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Match the roll in this station&apos;s printer. Stored on this machine.
                  </p>
                </div>
              </>
            )}

            <div className="flex items-start justify-between gap-6 border-t border-border pt-5">
              <div>
                <label htmlFor="station-active" className="text-sm font-medium">
                  Station is on
                </label>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Switch off a station you are not using today. Its categories go to another
                  station until it is back on.
                </p>
              </div>
              <Switch
                id="station-active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
          </SheetBody>

          <SheetFooter>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? 'Save station' : 'Add station'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Printer picker for a printer-output station. In the Bhookly desktop app it
 * lists the machine's real printers (so the admin maps a station to a device);
 * in a plain browser it degrades to a free-text input (printer name or
 * "ip:port"). Empty => OS default printer.
 */
function PrinterTargetField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [printers, setPrinters] = useState<{ name: string; isDefault: boolean }[] | null>(null)

  useEffect(() => {
    const d = desktop()
    if (!d) return
    let alive = true
    d.getPrinters()
      .then((list) => {
        if (alive) setPrinters(list)
      })
      .catch(() => {
        if (alive) setPrinters([])
      })
    return () => {
      alive = false
    }
  }, [])

  // Desktop app with detected printers: a dropdown of real devices.
  if (printers && printers.length > 0) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Printer"
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <option value="">Default printer</option>
        {printers.map((p) => (
          <option key={p.name} value={p.name}>
            {p.name}
            {p.isDefault ? ' (default)' : ''}
          </option>
        ))}
      </select>
    )
  }

  // Browser, or desktop with no detected printers: free-text fallback.
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Default printer"
      aria-label="Printer"
    />
  )
}

function DeleteStationDialog({
  station,
  categories,
  stations,
  onCancel,
  onConfirm,
  deleting,
}: {
  station: KitchenStation | null
  categories: Category[]
  stations: KitchenStation[]
  onCancel: () => void
  onConfirm: () => void
  deleting: boolean
}) {
  if (!station) return null
  const assigned = categories.filter((c) => (station.category_ids || []).includes(c.id))
  // After delete, where would items in those categories actually land?
  const remaining = stations.filter((s) => s.id !== station.id)
  const fallback = resolveRoutedStation(remaining, null)?.station

  return (
    <Dialog open={!!station} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {station.name}?</DialogTitle>
          <DialogDescription>The station is removed for good.</DialogDescription>
        </DialogHeader>
        {assigned.length > 0 ? (
          <div className="space-y-3 text-sm">
            <p>
              {countOf(assigned.length, 'category loses', 'categories lose')} its station:
            </p>
            <div className="flex flex-wrap gap-2">
              {assigned.map((c) => (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1"
                >
                  <CategoryDot color={c.color} />
                  {c.name}
                </span>
              ))}
            </div>
            {fallback ? (
              <p className="text-muted-foreground">
                {assigned.length === 1 ? 'It goes' : 'They go'} to{' '}
                <span className="font-medium text-foreground">{fallback.name}</span> until you place{' '}
                {assigned.length === 1 ? 'it' : 'them'} somewhere else.
              </p>
            ) : (
              <p className="text-warning-fg">
                No other station is on, so orders from{' '}
                {assigned.length === 1 ? 'this category' : 'these categories'} will have nowhere to
                go until you add one.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No categories are placed on this station.</p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={deleting}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete station
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MoveCategoryDialog({
  pendingMove,
  onCancel,
  onConfirm,
  moving,
}: {
  pendingMove: { category: Category; fromStation: KitchenStation; toStation: KitchenStation } | null
  onCancel: () => void
  onConfirm: () => void
  moving: boolean
}) {
  if (!pendingMove) return null
  const { category, fromStation, toStation } = pendingMove
  return (
    <Dialog open={!!pendingMove} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Move {category.name} to {toStation.name}?
          </DialogTitle>
          <DialogDescription>
            A category can only be on one station, so {category.name} will leave{' '}
            <span className="font-medium">{fromStation.name}</span>.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={onConfirm} disabled={moving}>
            <ArrowRight className="w-4 h-4 mr-2" /> Move category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
